import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

interface LoadingContextType {
  isLoading: boolean;
  loadingMessage: string | null;
  /** Progresso opcional (0-100). Quando definido, o overlay pode exibir barra de progresso. */
  loadingProgress: number | null;
  startLoading: (message?: string) => void;
  stopLoading: () => void;
  updateLoadingMessage: (message: string) => void;
  /** Atualiza o percentual de progresso (0-100). Usado em fluxos com etapas (ex.: signup). */
  updateLoadingProgress: (percent: number) => void;
  withLoading: <T>(fn: () => Promise<T>, message?: string) => Promise<T>;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

/**
 * Provider para gerenciar estado global de loading
 * Suporta múltiplos loadings simultâneos usando um contador
 */
export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [loadingCount, setLoadingCount] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<number | null>(null);

  const startLoading = useCallback((message?: string) => {
    setLoadingCount((prev) => {
      if (prev === 0) {
        setLoadingMessage(message || null);
        setLoadingProgress(null);
      }
      return prev + 1;
    });
  }, []);

  const stopLoading = useCallback(() => {
    setLoadingCount((prev) => {
      const newCount = Math.max(0, prev - 1);
      if (newCount === 0) {
        setLoadingMessage(null);
        setLoadingProgress(null);
      }
      return newCount;
    });
  }, []);

  const updateLoadingMessage = useCallback((message: string) => {
    setLoadingMessage(message);
  }, []);

  const updateLoadingProgress = useCallback((percent: number) => {
    setLoadingProgress(Math.min(100, Math.max(0, percent)));
  }, []);

  /**
   * Wrapper para executar uma função assíncrona com loading automático
   * @param fn Função assíncrona a ser executada
   * @param message Mensagem opcional para exibir durante o loading
   */
  const withLoading = useCallback(
    async <T,>(fn: () => Promise<T>, message?: string): Promise<T> => {
      startLoading(message);
      try {
        const result = await fn();
        return result;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading]
  );

  const value: LoadingContextType = useMemo(() => ({
    isLoading: loadingCount > 0,
    loadingMessage,
    loadingProgress,
    startLoading,
    stopLoading,
    updateLoadingMessage,
    updateLoadingProgress,
    withLoading,
  }), [loadingCount, loadingMessage, loadingProgress, startLoading, stopLoading, updateLoadingMessage, updateLoadingProgress, withLoading]);

  return <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>;
};

/**
 * Hook para acessar o contexto de loading
 * @throws Error se usado fora do LoadingProvider
 */
export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

